import { ArrowUpRight, MapPin } from "lucide-react";
import { Reveal, SectionHead } from "./shared";
import { PROJECTS } from "../data/content";

const ProjectImage = ({ project }) => (
    <div className="tech-frame relative aspect-[16/9] overflow-hidden border border-vec-border bg-vec-mist dot-grid">
        {project.image ? (
            <img
                src={project.image}
                alt={`${project.client} — ${project.scope}`}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
            />
        ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-center px-6">
                <span className="font-mono-tech text-[10px] uppercase tracking-[0.28em] text-vec-green/70">
                    Project Image
                </span>
                <span className="font-mono-tech text-[9px] uppercase tracking-[0.2em] text-vec-charcoal/35">
                    Photography to be updated
                </span>
            </div>
        )}
        <span className="absolute top-3 right-3 font-mono-tech text-[9px] uppercase tracking-[0.2em] text-vec-charcoal/40 bg-white/85 px-2 py-1">
            PRJ.{String(project.id).padStart(2, "0")}
        </span>
    </div>
);

const ProjectCard = ({ project }) => (
    <Reveal className="h-full">
        <article
            data-testid={`project-card-${project.id}`}
            className="group h-full flex flex-col border border-vec-border bg-white hover:border-vec-green/40 transition-colors duration-500"
        >
            <ProjectImage project={project} />
            <div className="flex flex-1 flex-col p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                    <span className="font-mono-tech text-[10px] uppercase tracking-[0.16em] text-vec-green">
                        {project.voltage} — {project.category}
                    </span>
                    <ArrowUpRight
                        size={16}
                        className="shrink-0 text-vec-charcoal/40 transition-all duration-300 group-hover:text-vec-green-bright group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                        aria-hidden="true"
                    />
                </div>
                <h3 className="mt-3 font-display text-xl font-semibold tracking-tight text-vec-charcoal leading-snug">
                    {project.client}
                </h3>
                <p className="mt-2 flex items-center gap-2 text-xs text-vec-charcoal/55">
                    <MapPin size={13} className="shrink-0 text-vec-green" aria-hidden="true" />
                    {project.location}
                </p>
                <ul className="mt-4 border-t border-vec-border flex-1">
                    {project.meta.map((m) => (
                        <li
                            key={m}
                            className="flex items-center gap-2.5 border-b border-vec-border py-2.5 text-xs sm:text-[13px] text-vec-charcoal/70"
                        >
                            <span className="h-1 w-1 shrink-0 rotate-45 bg-vec-green" aria-hidden="true" />
                            {m}
                        </li>
                    ))}
                </ul>
            </div>
        </article>
    </Reveal>
);

export const Projects = () => (
    <section id="projects" data-testid="projects-section" className="relative bg-white py-16 lg:py-32">
        <div className="vec-container">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
                <SectionHead
                    index="SEC 06"
                    eyebrow="Selected Projects"
                    title="Selected Projects"
                    sub="Selected project experience across power transmission and substation infrastructure."
                />
                <Reveal delay={0.2}>
                    <p className="font-mono-tech text-[10px] uppercase tracking-[0.22em] text-vec-charcoal/45 lg:text-right">
                        {String(PROJECTS.length).padStart(2, "0")} Engagements / Substation O&amp;M
                    </p>
                </Reveal>
            </div>

            <div className="mt-10 lg:mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6">
                {PROJECTS.map((p) => (
                    <ProjectCard key={p.id} project={p} />
                ))}
            </div>
        </div>
    </section>
);
